import React, { useEffect, useState, useCallback } from 'react';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { fmtDateTime, roleLabel } from './activityMeta';

const ago = (d) => {
  if (!d) return 'never';
  const s = Math.max(0, (Date.now() - new Date(d).getTime()) / 1000);
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

// Who has access, when each person last logged in / used the app, and what
// they did in the last 30 days — plus the raw login history (with failed attempts).
export default function SuperAdminStaff() {
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [staff, setStaff] = useState([]);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');

  useEffect(() => { superAdminApi.get('/shops').then(r => setShops(r.data.shops)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        superAdminApi.get('/insights/staff', { params: { shopId: shopId || undefined } }),
        superAdminApi.get('/insights/activity', { params: { shopId: shopId || undefined, userId: userFilter || undefined, action: 'login,login_failed', limit: 50 } }),
      ]);
      setStaff(s.data.staff); setLogins(l.data.activity);
    } catch { toast.error('Failed to load staff'); }
    setLoading(false);
  }, [shopId, userFilter]);
  useEffect(() => { load(); }, [load]);

  // Edit any login's name / email / role right from this list.
  const [edit, setEdit] = useState(null);
  const saveEdit = async () => {
    try {
      await superAdminApi.put(`/shop-users/${edit._id}`, { name: edit.name, email: edit.email, role: edit.role });
      toast.success('Login updated');
      setEdit(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
  };

  // Disabled = logged out immediately and blocked from every action until
  // enabled again here (the shop admin can't undo it from the app).
  const toggleAccess = async (u) => {
    const msg = u.isActive
      ? `Disable "${u.name}"?\n\nThey are logged out right away and can't add, edit or delete anything until you enable them again.`
      : `Enable "${u.name}" again?`;
    if (!window.confirm(msg)) return;
    try {
      await superAdminApi.put(`/shop-users/${u._id}/toggle`);
      toast.success(u.isActive ? 'Login disabled' : 'Login enabled');
      load();
    } catch { toast.error('Failed to update'); }
  };

  const Num = ({ v, color }) => <td style={{ textAlign: 'center', fontWeight: v ? 700 : 400, color: v ? color || '#0f172a' : '#cbd5e1' }}>{v}</td>;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="form-control" style={{ width: 220 }} value={shopId} onChange={e => { setShopId(e.target.value); setUserFilter(''); }}>
            <option value="">All Shops</option>
            {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          {userFilter && <button className="btn btn-outline btn-sm" onClick={() => setUserFilter('')}>Clear person filter ✕</button>}
        </div>
      </div>

      {loading ? <div className="loading"><div className="spinner"></div></div> : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><strong>Staff accounts</strong><span style={{ color: '#64748b', fontSize: 13 }}>{staff.length} people · last 30 days activity</span></div>
            <div className="table-container">
              <table>
                <thead><tr><th>Person</th><th>Shop</th><th>Last login</th><th>Last seen</th><th>Logins (total)</th><th>Bills</th><th>Orders</th><th>Payments</th><th>Edits</th><th>Deliveries</th><th>Deletes</th><th>Failed logins</th><th>Access</th></tr></thead>
                <tbody>
                  {staff.map(u => (
                    <tr key={u._id} style={{ cursor: 'pointer', background: userFilter === u._id ? '#eef2ff' : undefined }} onClick={() => setUserFilter(userFilter === u._id ? '' : u._id)}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{u.name} {!u.isActive && <span className="badge badge-gray">disabled</span>}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{roleLabel(u.role)} · {u.email}</div>
                      </td>
                      <td>{u.shop?.name || '—'}</td>
                      <td>{fmtDateTime(u.lastLoginAt)}<div style={{ fontSize: 11, color: '#94a3b8' }}>{ago(u.lastLoginAt)}</div></td>
                      <td>{fmtDateTime(u.lastSeenAt)}<div style={{ fontSize: 11, color: '#94a3b8' }}>{ago(u.lastSeenAt)}</div></td>
                      <td style={{ textAlign: 'center' }}>{u.loginCount}</td>
                      <Num v={u.last30.billsCreated} color="#8b5cf6" />
                      <Num v={u.last30.ordersCreated} color="#3b82f6" />
                      <Num v={u.last30.payments} color="#10b981" />
                      <Num v={u.last30.edits} color="#f59e0b" />
                      <Num v={u.last30.deliveries} color="#059669" />
                      <Num v={u.last30.deletes} color="#ef4444" />
                      <Num v={u.last30.failedLogins} color="#ef4444" />
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn btn-outline btn-sm" style={{ marginInlineEnd: 4 }}
                          onClick={() => setEdit({ _id: u._id, name: u.name, email: u.email, role: u.role })}>Edit</button>
                        <button className="btn btn-outline btn-sm"
                          style={u.isActive ? { color: '#b45309', borderColor: '#fde68a' } : { color: '#047857', borderColor: '#a7f3d0' }}
                          onClick={() => toggleAccess(u)}>
                          {u.isActive ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '8px 16px', fontSize: 12, color: '#94a3b8' }}>Click a person to filter the login history below. Login and activity counts start from when tracking went live.</div>
          </div>

          <div className="card">
            <div className="card-header"><strong>Login history</strong><span style={{ color: '#64748b', fontSize: 13 }}>latest 50</span></div>
            <div className="table-container">
              <table>
                <thead><tr><th>When</th><th>Person</th><th>Result</th><th>Shop</th><th>IP address</th><th>Device</th></tr></thead>
                <tbody>
                  {logins.length === 0 && <tr><td colSpan={6}><div className="empty-state"><h3>No logins recorded yet</h3></div></td></tr>}
                  {logins.map(a => (
                    <tr key={a._id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.createdAt)}</td>
                      <td>{a.userName || a.meta?.email || '—'}{a.userRole && <div style={{ fontSize: 11, color: '#94a3b8' }}>{roleLabel(a.userRole)}</div>}</td>
                      <td>{a.action === 'login'
                        ? <span className="badge badge-success">Logged in</span>
                        : <span className="badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>Failed attempt</span>}</td>
                      <td>{a.shop?.name || '—'}</td>
                      <td>{a.ip || '—'}</td>
                      <td style={{ fontSize: 12, color: '#64748b', maxWidth: 320 }} title={a.userAgent}>{a.userAgent || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {edit && (
        <div className="modal-overlay" onClick={() => setEdit(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">✏️ Edit Login</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setEdit(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-control" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email (used to log in with OTP)</label>
                <input type="email" className="form-control" value={edit.email} onChange={e => setEdit({ ...edit, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={edit.role} onChange={e => setEdit({ ...edit, role: e.target.value })}>
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                  <option value="cutter">Cutting (cutter)</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setEdit(null)}>Cancel</button>
              <button className="btn btn-accent" onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
